import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@web/core/query';
import { overviewRepository, triageInboxRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useSnackbar } from '@web/ui/core';
import { fireAndForget } from '@web/ui/core/utils';

const POLL_MS = 3000;
/** Stable polls before declaring the drain finished — `no_match` cards legitimately stay. */
const STABLE_POLLS_TO_FINISH = 3;

interface DrainWatch {
  /** `dataUpdatedAt` of the last sample, so re-renders don't count as polls. */
  lastAt: number;
  lastCount: number | null;
  stable: number;
}

/**
 * Bulk retriage kick + live progress. The kick is fire-and-forget on the
 * backend (no job state), so "running" is measured by its observable effect:
 * the triage box count draining. While `isRunning`, the page's own sidebar
 * count (same cache entry) is polled — the header total and the rows fall in
 * real time — until the count zeroes or stops moving, then a summary toast
 * closes the loop.
 */
export function useRetriage(orgId: string): {
  retriage: () => void;
  isPending: boolean;
  isRunning: boolean;
} {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [running, setRunning] = useState(false);
  const watch = useRef<DrainWatch>({ lastAt: 0, lastCount: null, stable: 0 });

  const invalidateInbox = (): void => {
    fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.triage.inbox(orgId) }));
  };

  const finish = (left: number): void => {
    setRunning(false);
    showSnackbar({
      severity: 'success',
      message:
        left === 0
          ? t('triage.retriage.done.empty')
          : t('triage.retriage.done.left', { count: left }),
    });
    invalidateInbox();
  };

  const mutation = useMutation({
    mutationFn: () => triageInboxRepository.retriageUntriaged(orgId),
    onSuccess: ({ requested }) => {
      showSnackbar({
        severity: 'success',
        message: t('triage.retriage.success', { count: requested }),
      });
      invalidateInbox();
      fireAndForget(
        queryClient.invalidateQueries({ queryKey: queryKeys.overview.sidebarCounts(orgId) }),
      );
      if (requested > 0) {
        watch.current = { lastAt: 0, lastCount: null, stable: 0 };
        setRunning(true);
      }
    },
    onError: () => {
      showSnackbar({ severity: 'error', message: t('triage.retriage.error') });
    },
  });

  // Drain watcher: same query (and cache entry) the page renders, with an
  // interval that re-arms after each sample and stops on zero / stability.
  useQuery({
    queryKey: queryKeys.overview.sidebarCounts(orgId),
    queryFn: () => overviewRepository.loadSidebarCounts(orgId),
    enabled: running,
    refetchInterval: (query) => {
      if (!running) return false;
      const { data, dataUpdatedAt } = query.state;
      if (data === undefined) return POLL_MS;
      const w = watch.current;
      if (dataUpdatedAt === w.lastAt) return POLL_MS; // same sample, not a new poll
      w.lastAt = dataUpdatedAt;

      const count = data.triage;
      if (count === 0) {
        finish(0);
        return false;
      }
      if (w.lastCount === count) {
        w.stable += 1;
        if (w.stable >= STABLE_POLLS_TO_FINISH) {
          finish(count);
          return false;
        }
      } else {
        // The box moved — keep the visible rows in sync even without SSE.
        if (w.lastCount !== null) invalidateInbox();
        w.lastCount = count;
        w.stable = 0;
      }
      return POLL_MS;
    },
  });

  return {
    retriage: () => {
      mutation.mutate();
    },
    isPending: mutation.isPending,
    isRunning: running,
  };
}
